import React, { useEffect, useState, useCallback } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Spinner,
} from "reactstrap";
import axios from "axios";
import { signupSupporter } from "../../services/supporter";
import "./index.css";

const BE_URL = process.env.REACT_APP_BE_URL;
const MOVEMENT = process.env.REACT_APP_MOVEMENT;

// Passwordless one-screen signup (#2). Collect name/email/state/county (zone
// only if the county actually has zones), POST /signup, then show "check your
// email". The emailed link → /confirm creates the supporter. No password, no
// code to paste, no captcha.
const emailOk = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(e || "").trim());
// userName: matches backend Joi — >=4 chars, words separated by single spaces.
const userNameOk = (u) =>
  /^\w+(?:\s+\w+)*$/.test(String(u || "").trim()) && String(u || "").trim().length >= 4;

const SignupModal = (props) => {
  const { isOpen } = props;
  const close = useCallback(() => {
    (props.toggleVisibility || props.toggleModel || props.toggle || (() => {}))();
    if (props.resetForm) props.resetForm();
  }, [props]);

  const [userName, setUserName] = useState(props.userName || "");
  const [email, setEmail] = useState(props.email || "");
  const [state, setState] = useState(props.state || "");
  const [county, setCounty] = useState(props.county || "");
  const [zone, setZone] = useState(props.precinct || "");
  const [coreSupporter, setCoreSupporter] = useState(true);

  const [statesList, setStatesList] = useState([]);
  const [countyList, setCountyList] = useState([]);
  const [zoneList, setZoneList] = useState([]);

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false); // "check your email" screen

  // Load states once the modal opens.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${BE_URL}/api/total/${MOVEMENT}/USA`);
        if (!cancelled && data && data.subordinates)
          setStatesList(Object.keys(data.subordinates).sort());
      } catch (e) {
        /* leave empty */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  // Load counties when a state is chosen.
  useEffect(() => {
    if (!state) {
      setCountyList([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data } = await axios.get(
          `${BE_URL}/api/total/${MOVEMENT}/USA/${state}`
        );
        if (!cancelled && data && data.subordinates)
          setCountyList(Object.keys(data.subordinates).sort());
      } catch (e) {
        if (!cancelled) setCountyList([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [state]);

  // Load zones for the chosen county. The dropdown only shows if the county
  // actually has sub-zones (data-driven); otherwise zone silently = county.
  useEffect(() => {
    if (!state || !county) {
      setZoneList([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(
          `${BE_URL}/api/total/${MOVEMENT}/USA/${state}/${county}`
        );
        if (!cancelled && data && data.subordinates) {
          const zones = Object.keys(data.subordinates).filter(
            (z) => z && z !== county
          );
          setZoneList(zones);
        }
      } catch (e) {
        if (!cancelled) setZoneList([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [state, county]);

  const hasZones = zoneList.length > 0;
  const formValid = userNameOk(userName) && emailOk(email) && state && county;

  const submit = async () => {
    setError("");
    if (!userNameOk(userName)) {
      setError("Please enter a name of at least 4 letters (letters, numbers, spaces).");
      return;
    }
    if (!emailOk(email)) {
      setError("Please enter a valid email address.");
      return;
    }
    if (!state || !county) {
      setError("Please choose your state and county.");
      return;
    }
    setSubmitting(true);
    const res = await signupSupporter({
      userName: userName.trim(),
      email: email.trim(),
      state,
      county,
      precinct: hasZones && zone ? zone : county,
      coreSupporter,
    });
    setSubmitting(false);
    if (res.error) {
      setError(typeof res.error === "string" ? res.error : "Something went wrong. Please try again.");
      return;
    }
    setSent(true);
  };

  const onClose = () => {
    // reset for next open
    setSent(false);
    setError("");
    close();
  };

  return (
    <Modal
      backdrop="static"
      isOpen={isOpen}
      toggle={onClose}
      centered
      modalClassName="signup-wrapper"
      className="signup-modal"
    >
      <ModalHeader toggle={onClose}>
        {sent ? "ALMOST THERE" : "REGISTER YOUR SUPPORT"}
      </ModalHeader>
      <ModalBody>
        {sent ? (
          <div style={{ textAlign: "center", padding: "8px 4px" }}>
            <div style={{ fontSize: 44, marginBottom: 8 }}>📬</div>
            <h5 style={{ color: "#5A8A8E" }}>Check your email</h5>
            <p style={{ margin: "8px 0 4px" }}>
              We sent a confirmation link to <strong>{email}</strong>. Click it to add your
              support to the map.
            </p>
            <p style={{ fontSize: 13, color: "#6b7680" }}>
              It may land in your spam/junk folder. The link is good for 24 hours.
            </p>
            <Button color="secondary" size="sm" onClick={onClose} className="mt-2">
              Done
            </Button>
          </div>
        ) : (
          <Form onSubmit={(e) => e.preventDefault()}>
            <FormGroup>
              <Label for="su-name">Your name</Label>
              <Input
                id="su-name"
                type="text"
                value={userName}
                placeholder="How we'll refer to you"
                onChange={(e) => setUserName(e.target.value)}
              />
            </FormGroup>
            <FormGroup>
              <Label for="su-email">Email</Label>
              <Input
                id="su-email"
                type="email"
                value={email}
                placeholder="you@example.com"
                onChange={(e) => setEmail(e.target.value)}
              />
            </FormGroup>
            <FormGroup>
              <Label for="su-state">State</Label>
              <Input
                id="su-state"
                type="select"
                value={state}
                onChange={(e) => {
                  setState(e.target.value);
                  setCounty("");
                  setZone("");
                }}
              >
                <option value="">Select your state</option>
                {statesList.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </Input>
            </FormGroup>
            <FormGroup>
              <Label for="su-county">County</Label>
              <Input
                id="su-county"
                type="select"
                value={county}
                disabled={!state || loading}
                onChange={(e) => {
                  setCounty(e.target.value);
                  setZone("");
                }}
              >
                <option value="">
                  {loading ? "Loading counties…" : "Select your county"}
                </option>
                {countyList.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </Input>
            </FormGroup>
            {hasZones && (
              <FormGroup>
                <Label for="su-zone">Voting zone</Label>
                <Input
                  id="su-zone"
                  type="select"
                  value={zone}
                  onChange={(e) => setZone(e.target.value)}
                >
                  <option value="">{county} (whole county)</option>
                  {zoneList.map((z) => (
                    <option key={z} value={z}>
                      {z}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            )}
            <FormGroup check className="mb-2">
              <Label check style={{ fontSize: 14 }}>
                <Input
                  type="checkbox"
                  checked={coreSupporter}
                  onChange={() => setCoreSupporter((v) => !v)}
                />{" "}
                Keep me posted with important updates and surveys on draft legislation.
              </Label>
            </FormGroup>
            {error && (
              <div style={{ color: "#dc3545", fontSize: 14, marginBottom: 8 }}>{error}</div>
            )}
            <Button
              color="danger"
              block
              disabled={!formValid || submitting}
              onClick={submit}
            >
              {submitting ? (
                <>
                  <Spinner size="sm" color="light" className="mr-2" /> Sending…
                </>
              ) : (
                "Add my support"
              )}
            </Button>
            <p style={{ fontSize: 12, color: "#6b7680", marginTop: 10, marginBottom: 0 }}>
              We'll email you a one-click link to confirm — no password needed.
            </p>
          </Form>
        )}
      </ModalBody>
    </Modal>
  );
};

export default SignupModal;
